import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import HttpService from "../../hooks/Https-services";
import PageRoutes, { APIRoutes } from "../../utils/constants";
import useConfig from "../../hooks/useConfig";
import { Logout } from "../../hooks/useAxiosPrivate";

// HTPTV-636: recipient redeem/activation landing page (public). The gift binds
// only to the invited email; the backend enforces that on redeem, so new
// recipients sign up with their address and existing ones log in with it.
const REASON_MESSAGES: Record<string, string> = {
  already_redeemed: "This gift has already been activated.",
  expired: "This gift has expired.",
  not_delivered: "This gift isn't available to activate yet.",
};

const TIER_DESC: Record<string, string> = {
  basic: "Every game, live and on demand, across all leagues.",
  insider:
    "All games plus personalized channels, watch parties and clip downloads.",
};

const termLong = (billing: string, term: number) => {
  const unit = billing === "annual" ? "year" : "month";
  return `${term} ${unit}${term > 1 ? "s" : ""}`;
};

const termShort = (billing: string, term: number) => {
  const unit = billing === "annual" ? "YR" : "MO";
  return `${term} ${unit}${term > 1 ? "S" : ""}`;
};

const RedeemGift = () => {
  const { token }: any = useParams();
  const navigate = useNavigate();
  const configData = useConfig();
  const primary = configData?.primary;

  const [loading, setLoading] = useState(true);
  const [gift, setGift] = useState<any>(null);
  const [reason, setReason] = useState<string>("");
  const [notFound, setNotFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [doneDate, setDoneDate] = useState<string | null>(null);
  // HTPTV-636: redeemed while the recipient already had coverage (a paid plan
  // or an earlier gift) -> the gift is queued and starts when that coverage ends.
  const [queued, setQueued] = useState(false);
  // HTPTV-636: when a logged-in user views the page, the backend reports whether
  // the gift is addressed to their email (null = not logged in). false -> hide
  // the Activate button (it would only 403) and tell them to use the right account.
  const [forCurrentUser, setForCurrentUser] = useState<boolean | null>(null);
  // HTPTV-636 Option B: if the recipient already has an active paid plan,
  // activating the gift stops that plan from auto-renewing and the gift takes
  // over when the current paid period ends. We ask for consent first.
  const [hasPaidPlan, setHasPaidPlan] = useState(false);
  const [planRenewsOn, setPlanRenewsOn] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [startsOn, setStartsOn] = useState<string | null>(null);

  const isLoggedIn = (() => {
    try {
      return Boolean(JSON.parse(localStorage.getItem("auth") || "{}")?.AccessToken);
    } catch {
      return false;
    }
  })();

  useEffect(() => {
    (async () => {
      try {
        const res = await HttpService.get(
          APIRoutes.GetGiftRedeemInfo.replace(":token", token)
        );
        const d = res?.data?.data;
        setGift(d?.gift || null);
        setForCurrentUser(
          d?.forCurrentUser === undefined ? null : d.forCurrentUser
        );
        setHasPaidPlan(Boolean(d?.viewer?.hasActivePaidPlan));
        setPlanRenewsOn(d?.viewer?.planRenewsOn || null);
        if (!d?.redeemable) setReason(d?.reason || "unavailable");
      } catch (e: any) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleRedeem = async () => {
    setSubmitting(true);
    try {
      const res = await HttpService.post(
        APIRoutes.RedeemGiftSubscription.replace(":token", token),
        {}
      );
      const d = res?.data?.data;
      setQueued(Boolean(d?.queued));
      setStartsOn(d?.startsOn || null);
      setDoneDate(d?.entitlementEnd || "");
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? "Unable to activate this gift.");
    } finally {
      setSubmitting(false);
    }
  };

  // Plain centered card for loading / error / success states (no hero banner).
  const PlainShell = ({ children }: { children: React.ReactNode }) => (
    <Box sx={{ minHeight: "100vh", bgcolor: "action.hover", display: "flex", alignItems: "center", justifyContent: "center", p: 2, "& *": { fontStyle: "normal !important", textTransform: "none !important" } }}>
      <Paper elevation={2} sx={{ borderRadius: "16px", maxWidth: 480, width: "100%", p: { xs: 3, md: 5 }, textAlign: "center" }}>
        {children}
      </Paper>
    </Box>
  );

  if (loading) {
    return (
      <PlainShell>
        <CircularProgress sx={{ color: primary }} />
      </PlainShell>
    );
  }

  if (notFound) {
    return (
      <PlainShell>
        <Typography variant="h6" fontWeight={800} mb={1}>Gift not found</Typography>
        <Typography variant="body2" color="text.secondary">This link is invalid. Please check the link in your email.</Typography>
      </PlainShell>
    );
  }

  if (doneDate !== null) {
    return (
      <PlainShell>
        <CheckCircleOutlineIcon sx={{ color: primary, fontSize: 48, mb: 1 }} />
        <Typography variant="h6" fontWeight={800} mb={1}>You're all set!</Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          {queued
            ? `Your gift is saved. ${
                hasPaidPlan ? "Your current plan won't renew, and your" : "Your"
              } gift starts ${
                startsOn
                  ? `on ${dayjs(startsOn).format("MMM D, YYYY")}`
                  : "when your current access ends"
              }.`
            : `Your access is active${
                doneDate ? ` through ${dayjs(doneDate).format("MMM D, YYYY")}` : ""
              }. Enjoy the games.`}
        </Typography>
        <Button fullWidth variant="contained" sx={{ bgcolor: primary, ":hover": { bgcolor: primary } }} onClick={() => navigate(PageRoutes.HomePage)}>
          {queued ? "Go to home" : "Start watching"}
        </Button>
      </PlainShell>
    );
  }

  if (reason) {
    return (
      <PlainShell>
        <Typography variant="h6" fontWeight={800} mb={1}>Gift unavailable</Typography>
        <Typography variant="body2" color="text.secondary">{REASON_MESSAGES[reason] || "This gift can't be activated."}</Typography>
      </PlainShell>
    );
  }

  const tier = gift?.planTier
    ? gift.planTier.charAt(0).toUpperCase() + gift.planTier.slice(1)
    : "";
  const isWrongAccount = isLoggedIn && forCurrentUser === false;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "action.hover", display: "flex", alignItems: "center", justifyContent: "center", p: 2, "& *": { fontStyle: "normal !important", textTransform: "none !important" } }}>
      <Paper elevation={3} sx={{ borderRadius: "20px", maxWidth: 560, width: "100%", overflow: "hidden" }}>
        {/* Hero banner */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #15264d 0%, #0e1a35 100%)",
            px: 3,
            py: { xs: 3.5, md: 4.5 },
            textAlign: "center",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box sx={{ position: "absolute", top: -40, right: -30, width: 160, height: 160, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.05)" }} />
          <Box sx={{ position: "absolute", bottom: -50, left: -20, width: 140, height: 140, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.04)" }} />
          <Typography sx={{ color: "rgba(255,255,255,0.85)", fontWeight: 700, letterSpacing: 3, fontSize: "0.8rem" }}>
            {isWrongAccount ? `${configData.personalChannel} GIFT` : "CONGRATULATIONS"}
          </Typography>
          <Box sx={{ fontSize: "2.6rem", lineHeight: 1, my: 1.5 }}>🎁</Box>
          <Stack direction="row" spacing={1.25} alignItems="center" justifyContent="center">
            <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: { xs: "1.4rem", md: "1.7rem" } }}>
              {isWrongAccount ? "This gift is for" : "Welcome to"}
            </Typography>
            {!isWrongAccount && configData?.logoUrl && (
              <Box sx={{ bgcolor: "#fff", borderRadius: "8px", px: 1.25, py: 0.75, display: "flex", alignItems: "center" }}>
                <img src={configData.logoUrl} alt={configData.personalChannel} style={{ height: 26, objectFit: "contain", maxWidth: 110 }} />
              </Box>
            )}
          </Stack>
        </Box>

        {/* Body */}
        <Box sx={{ px: { xs: 3, md: 5 }, py: { xs: 3, md: 4 }, textAlign: "center" }}>
          {isWrongAccount ? (
            <Typography variant="body1" color="text.primary" mb={3}>
              This gift is for a different account. It was sent to a different
              email address; sign in with that address to activate it.
            </Typography>
          ) : (
            <Typography variant="body1" color="text.primary" mb={3} sx={{ lineHeight: 1.6 }}>
              You've been gifted a{" "}
              <strong>
                {termLong(gift?.billingModel, gift?.termCount || 1)} {tier} subscription
              </strong>
              : every game, live and on demand.
            </Typography>
          )}

          {/* Plan card */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2, mb: 2.5, border: "1px solid", borderColor: "divider", borderRadius: "12px", bgcolor: "action.hover", textAlign: "left" }}>
            <Box sx={{ flexShrink: 0, width: 64, border: "1px solid", borderColor: "divider", borderRadius: "8px", bgcolor: "#fff", p: 0.75, textAlign: "center" }}>
              {configData?.logoUrl && (
                <img src={configData.logoUrl} alt={configData.personalChannel} style={{ height: 22, objectFit: "contain", maxWidth: "100%" }} />
              )}
              <Typography sx={{ mt: 0.5, fontWeight: 800, fontSize: "0.55rem", letterSpacing: 0.5, color: primary }}>
                {(gift?.planTier || "").toUpperCase()} · {termShort(gift?.billingModel, gift?.termCount || 1)}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography fontWeight={800} color={primary}>{tier} Plan</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.4 }}>
                {TIER_DESC[gift?.planTier] || ""}
              </Typography>
            </Box>
            {gift?.buyerName && (
              <Box sx={{ flexShrink: 0, textAlign: "right" }}>
                <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 1, display: "block" }}>FROM</Typography>
                <Typography fontWeight={700} variant="body2">{gift.buyerName}</Typography>
              </Box>
            )}
          </Box>

          {/* Personal message */}
          {gift?.personalMessage && !isWrongAccount && (
            <Box sx={{ borderLeft: "3px solid", borderColor: primary, pl: 2, py: 0.5, mb: 3, textAlign: "left" }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic !important" }}>"{gift.personalMessage}"</Typography>
            </Box>
          )}

          {/* Action area */}
          {isWrongAccount ? (
            <Button
              fullWidth
              variant="outlined"
              sx={{ color: primary, borderColor: primary, py: 1.25 }}
              onClick={() => {
                Logout();
                navigate(PageRoutes.Login, {
                  state: { from: window.location.pathname, search: window.location.search },
                });
              }}
            >
              Use a different account
            </Button>
          ) : isLoggedIn ? (
            confirming ? (
              // Option B consent: recipient already has an active paid plan.
              <Stack spacing={1.5}>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  You currently have an active plan. Activating this gift will stop
                  it from auto-renewing, so you won't be charged again. Your gift
                  begins{" "}
                  {planRenewsOn
                    ? `on ${dayjs(planRenewsOn).format("MMM D, YYYY")}`
                    : "when your current plan period ends"}{" "}
                  and runs for {termLong(gift?.billingModel, gift?.termCount || 1)}.
                </Typography>
                <Button
                  fullWidth
                  variant="contained"
                  disabled={submitting}
                  sx={{ bgcolor: primary, ":hover": { bgcolor: primary }, py: 1.5, fontWeight: 700 }}
                  onClick={handleRedeem}
                >
                  {submitting ? "Activating…" : "Yes, activate my gift"}
                </Button>
                <Button
                  fullWidth
                  variant="text"
                  sx={{ color: "text.secondary" }}
                  disabled={submitting}
                  onClick={() => setConfirming(false)}
                >
                  Keep my current plan for now
                </Button>
              </Stack>
            ) : (
              <>
                <Button
                  fullWidth
                  variant="contained"
                  disabled={submitting}
                  sx={{ bgcolor: primary, ":hover": { bgcolor: primary }, py: 1.5, fontWeight: 700, fontSize: "1rem" }}
                  onClick={() => (hasPaidPlan ? setConfirming(true) : handleRedeem())}
                >
                  {submitting ? "Activating…" : "Activate My Subscription"}
                </Button>
                <Typography variant="caption" color="text.secondary" display="block" mt={2} sx={{ lineHeight: 1.6 }}>
                  {hasPaidPlan
                    ? "You already have a plan. Activating uses your gift and stops that plan from renewing, so you won't be charged again."
                    : "No payment needed: your gift is already paid for. We'll remind you before it ends, with the option to keep watching."}
                </Typography>
              </>
            )
          ) : (
            <Stack spacing={1.5}>
              <Button
                fullWidth
                variant="contained"
                sx={{ bgcolor: primary, ":hover": { bgcolor: primary }, py: 1.5, fontWeight: 700 }}
                onClick={() => navigate(`${PageRoutes.Register}?giftToken=${encodeURIComponent(token)}`)}
              >
                Create an account to activate
              </Button>
              <Button
                fullWidth
                variant="outlined"
                sx={{ color: primary, borderColor: primary }}
                onClick={() =>
                  navigate(PageRoutes.Login, {
                    state: { from: window.location.pathname, search: window.location.search },
                  })
                }
              >
                I already have an account
              </Button>
              <Typography variant="caption" color="text.secondary">
                Use the email address this gift was sent to.
              </Typography>
            </Stack>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default RedeemGift;
